/**
 * CompanyCreatePage — refonte 9.P.2.R1 UX/layout revamp.
 *
 * Pattern UX commun (réutilisable Edit + R2/R3) :
 *   - Layout 2-col desktop : section nav sidebar gauche + form principal droite
 *   - Sticky save bar bottom (Cancel + Save toujours visibles)
 *   - Confirmation unsaved changes via `confirm()` natif au Cancel
 *   - <details open> sections collapsibles préservés (HTML5 a11y native, Q5 PO)
 *     mais summary moderne (chevron + hover state)
 *
 * Logique métier strictement préservée :
 *   - D-NEW-D Phase 1 cross-field validation (identification_type × business_number)
 *   - onBlur/onChange revalidation + helper dynamique
 *   - aria-invalid / aria-describedby
 *   - localErrors frontend (precedence over fieldErrors API)
 *   - Auto-expand <details> au submit invalide
 *   - Focus + scroll vers premier champ en erreur
 *   - Backend fieldErrors pipeline + bandeau bilingue
 *   - Schemas.CreateCompanySchema safeParse pre-submit
 */

import { Schemas } from "@kollectivia/shared";
import { type FormEvent, type SyntheticEvent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { type ZodIssue, type ZodTypeAny } from "zod";

import { ApiException } from "../../lib/api";
import { construireRetour, estDestinationConnue } from "../../lib/retourParcours";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { useCreateCompany } from "../../lib/queries/companies";
import { useUnsavedChangesGuard } from "../../lib/unsavedChanges/UnsavedChangesContext";

import { CompanyFormDetails } from "./_components/CompanyFormDetails";
import { CompanySectionNav } from "./_components/CompanySectionNav";

// 9.T.4 post-smoke fix 3e itération — shape des candidats duplicate remontés
// par le backend POST /v1/companies quand le nouveau guard
// COMPANIES_CREATE_DUPLICATE_DETECTED déclenche (parité MaterializeEventDialog).
type DuplicateCandidate = {
  readonly company_id: string;
  readonly legal_name: string;
  readonly business_number: string | null;
  readonly address_city: string | null;
  readonly address_postal: string | null;
  readonly match_kind: "business_number_exact" | "name_postal_exact";
  readonly confidence: number;
};

type FormState = {
  legal_name: string;
  trade_name: string;
  business_number: string;
  identification_type: "" | "NEQ" | "BN" | "CORP_NUM_CA" | "EIN_US" | "VAT_EU" | "OTHER";
  industry: string;
  founded_year: string;
  address_street: string;
  address_city: string;
  address_province: string;
  address_postal: string;
  address_country: string;
  phone: string;
  fax: string;
  email: string;
  website: string;
  plan_admin_name: string;
  plan_admin_email: string;
  language: "fr" | "en";
  employee_count: string;
  has_seasonal: boolean;
  has_commission: boolean;
  has_unionized: boolean;
  has_remote_abroad: boolean;
  considers_remote_abroad: boolean;
  family_ties: boolean;
  family_ties_note: string;
  has_current_plan: boolean;
  current_insurer: string;
  current_policy_number: string;
  current_since_date: string;
  current_renewal_date: string;
  notes: string;
};

const EMPTY: FormState = {
  legal_name: "",
  trade_name: "",
  business_number: "",
  identification_type: "",
  industry: "",
  founded_year: "",
  address_street: "",
  address_city: "",
  address_province: "",
  address_postal: "",
  address_country: "",
  phone: "",
  fax: "",
  email: "",
  website: "",
  plan_admin_name: "",
  plan_admin_email: "",
  language: "fr",
  employee_count: "",
  has_seasonal: false,
  has_commission: false,
  has_unionized: false,
  has_remote_abroad: false,
  considers_remote_abroad: false,
  family_ties: false,
  family_ties_note: "",
  has_current_plan: false,
  current_insurer: "",
  current_policy_number: "",
  current_since_date: "",
  current_renewal_date: "",
  notes: "",
};

function toPayload(s: FormState): Record<string, unknown> {
  const str = (v: string) => (v.trim() === "" ? undefined : v.trim());
  const num = (v: string) => {
    const t = v.trim();
    if (t === "") return undefined;
    const n = Number(t);
    return Number.isFinite(n) ? n : undefined;
  };

  return {
    legal_name: s.legal_name.trim(),
    trade_name: str(s.trade_name),
    business_number: str(s.business_number),
    identification_type: s.identification_type === "" ? undefined : s.identification_type,
    industry: str(s.industry),
    founded_year: num(s.founded_year),
    address_street: str(s.address_street),
    address_city: str(s.address_city),
    address_province: str(s.address_province),
    address_postal: str(s.address_postal),
    address_country: str(s.address_country),
    phone: str(s.phone),
    fax: str(s.fax),
    email: str(s.email),
    website: str(s.website),
    plan_admin_name: str(s.plan_admin_name),
    plan_admin_email: str(s.plan_admin_email),
    language: s.language,
    employee_count: num(s.employee_count),
    has_seasonal: s.has_seasonal,
    has_commission: s.has_commission,
    has_unionized: s.has_unionized,
    has_remote_abroad: s.has_remote_abroad,
    considers_remote_abroad: s.considers_remote_abroad,
    family_ties: s.family_ties,
    family_ties_note: s.family_ties ? str(s.family_ties_note) : undefined,
    has_current_plan: s.has_current_plan,
    current_insurer: s.has_current_plan ? str(s.current_insurer) : undefined,
    current_policy_number: s.has_current_plan ? str(s.current_policy_number) : undefined,
    current_since_date: s.has_current_plan ? str(s.current_since_date) : undefined,
    current_renewal_date: s.has_current_plan ? str(s.current_renewal_date) : undefined,
    notes: str(s.notes),
  };
}

type SectionKey = "identity" | "address" | "contact" | "workforce" | "current_plan" | "notes";

const SECTION_OF_FIELD: Record<string, SectionKey> = {
  legal_name: "identity",
  trade_name: "identity",
  business_number: "identity",
  industry: "identity",
  founded_year: "identity",
  language: "identity",
  employee_count: "identity",
  address_street: "address",
  address_city: "address",
  address_province: "address",
  address_postal: "address",
  address_country: "address",
  phone: "contact",
  fax: "contact",
  email: "contact",
  website: "contact",
  plan_admin_name: "contact",
  plan_admin_email: "contact",
  family_ties_note: "workforce",
  current_insurer: "current_plan",
  current_policy_number: "current_plan",
  current_since_date: "current_plan",
  current_renewal_date: "current_plan",
  notes: "notes",
};

const FIELD_ORDER: ReadonlyArray<keyof FormState> = [
  "legal_name",
  "trade_name",
  "business_number",
  "industry",
  "founded_year",
  "language",
  "employee_count",
  "address_street",
  "address_city",
  "address_province",
  "address_postal",
  "address_country",
  "phone",
  "fax",
  "email",
  "website",
  "plan_admin_name",
  "plan_admin_email",
  "family_ties_note",
  "current_insurer",
  "current_policy_number",
  "current_since_date",
  "current_renewal_date",
  "notes",
];

const NUMERIC_FIELDS = new Set<string>(["founded_year", "employee_count"]);

function normalizeFieldValue(key: string, raw: unknown): unknown {
  if (typeof raw !== "string") return raw;
  const trimmed = raw.trim();
  if (trimmed === "") return undefined;
  if (NUMERIC_FIELDS.has(key)) {
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : trimmed;
  }
  return trimmed;
}

function validateField(key: keyof FormState, form: FormState): string[] {
  // D-NEW-D Phase 1 — cross-field validation business_number × identification_type.
  if (key === "business_number" && form.identification_type && form.business_number.trim() !== "") {
    const err = Schemas.validateBusinessNumberForType(
      form.business_number.trim(),
      form.identification_type,
    );
    if (err === "invalid_format") {
      return [`Invalid format for identification_type=${form.identification_type}`];
    }
  }

  const shape = Schemas.CreateCompanySchema.shape as Record<string, ZodTypeAny>;
  const fieldSchema = shape[key as string];
  if (!fieldSchema) return [];
  const value = normalizeFieldValue(key as string, form[key]);
  const result = fieldSchema.safeParse(value);
  if (result.success) return [];
  return result.error.issues.map((i) => i.message);
}

function zodIssuesToLocalErrors(issues: ZodIssue[]): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const issue of issues) {
    const key = issue.path.length === 0 ? "_form" : String(issue.path[0]);
    (out[key] ??= []).push(issue.message);
  }
  return out;
}

export function CompanyCreatePage() {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const navigate = useNavigate();
  // Pre-fill depuis query params si le broker arrive depuis un flow amont
  // qui a déjà collecté des données (ex : lien Step1Company du wizard
  // /projects/new — post-swap 9.T.4 3e itération). Allowlist stable :
  // legal_name / postal_code / business_number. Si aucun query param
  // fourni, le form démarre vide (comportement standard).
  const [searchParams] = useSearchParams();
  const initialForm = useMemo<FormState>(() => {
    const seed: Partial<FormState> = {};
    const legalName = searchParams.get("legal_name");
    if (legalName && legalName.trim()) seed.legal_name = legalName.trim();
    const postal = searchParams.get("postal_code");
    if (postal && postal.trim()) seed.address_postal = postal.trim().toUpperCase();
    const businessNum = searchParams.get("business_number");
    if (businessNum && businessNum.trim()) seed.business_number = businessNum.trim();
    return { ...EMPTY, ...seed };
  }, [searchParams]);
  const [form, setForm] = useState<FormState>(initialForm);
  const [dirty, setDirty] = useState(false);
  const create = useCreateCompany();

  const [localErrors, setLocalErrors] = useState<Record<string, string[]>>({});

  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>({
    identity: true,
    address: false,
    contact: false,
    workforce: false,
    current_plan: false,
    notes: false,
  });

  const focusTargetRef = useRef<string | null>(null);
  const formRef = useRef<FormState>(form);
  formRef.current = form;

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((s) => ({ ...s, [key]: value }));
    setDirty(true);
    setLocalErrors((errs) => {
      if (!(key in errs)) return errs;
      const next = { ...errs };
      delete next[key as string];
      return next;
    });
  };

  const handleBlur = (key: keyof FormState) => () => {
    const msgs = validateField(key, formRef.current);
    setLocalErrors((errs) => {
      const next = { ...errs };
      if (msgs.length === 0) {
        delete next[key as string];
      } else {
        next[key as string] = msgs;
      }
      return next;
    });
  };

  // 9.P.2.R2 — guard global unsaved changes. Le hook s'enregistre auprès du
  // <UnsavedChangesProvider> rendered par App.tsx RootLayout. Couvre :
  // navigation interne (NavLink AppShell, Link Detail→Edit), browser back/
  // forward (popstate), close tab / reload (beforeunload). Le Cancel button
  // déclenche `navigate(...)` qui passe par useBlocker → dialog auto.
  const { allowNavigation } = useUnsavedChangesGuard(dirty, t("companies.create.unsaved_message"));

  const onCancel = () => navigate("/companies");

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.legal_name.trim()) return;

    if (form.identification_type && form.business_number.trim() !== "") {
      const err = Schemas.validateBusinessNumberForType(
        form.business_number.trim(),
        form.identification_type,
      );
      if (err === "invalid_format") {
        setLocalErrors((errs) => ({
          ...errs,
          business_number: [`Invalid format for identification_type=${form.identification_type}`],
        }));
        setOpenSections((prev) => ({ ...prev, identity: true }));
        focusTargetRef.current = "business_number";
        return;
      }
    }

    const payload = toPayload(form);
    const result = Schemas.CreateCompanySchema.safeParse(payload);
    if (!result.success) {
      const errs = zodIssuesToLocalErrors(result.error.issues);
      setLocalErrors(errs);
      const sectionsToOpen = new Set<SectionKey>();
      for (const k of Object.keys(errs)) {
        const sec = SECTION_OF_FIELD[k];
        if (sec) sectionsToOpen.add(sec);
      }
      if (sectionsToOpen.size > 0) {
        setOpenSections((prev) => {
          const next = { ...prev };
          for (const s of sectionsToOpen) next[s] = true;
          return next;
        });
      }
      const firstErrorKey = FIELD_ORDER.find((k) => errs[k as string]?.length);
      focusTargetRef.current = firstErrorKey ? String(firstErrorKey) : null;
      return;
    }

    try {
      const created = await create.mutateAsync(result.data as Record<string, unknown>);
      // 9.P.2.R2 fix : bypass single-shot du blocker avant navigate post-save.
      // setDirty(false) seul ne suffit pas — il est queue-é par React batching
      // et navigate(...) s'exécute avant que useEffect du hook propage au
      // Provider, donc useBlocker voit encore dirty=true et ouvre le dialog.
      allowNavigation();
      setDirty(false);
      // 9.BA.1 — si l'on vient d'un parcours, on y RETOURNE, avec l'entreprise
      // déjà sélectionnée et l'intention préservée. La destination vient d'une
      // liste blanche : un `retour` inconnu ne redirige nulle part, il retombe
      // sur la fiche. Un paramètre de retour non borné serait une redirection
      // ouverte — il suffirait d'un lien pour faire rebondir un utilisateur
      // authentifié hors du produit.
      // 9.BB.1 — la construction peut désormais REFUSER (destination visant une
      // ressource dont l'identifiant n'est pas un UUID). Un refus n'est pas une
      // erreur à afficher : c'est un retour qu'on ne fait pas, et on retombe sur
      // la fiche, comme pour une destination inconnue.
      const retour = searchParams.get("retour");
      const cible = estDestinationConnue(retour)
        ? construireRetour({
            destination: retour,
            companyId: created.id,
            intention: searchParams.get("intention"),
            id: searchParams.get("retour_id"),
          })
        : null;
      navigate(cible ?? `/companies/${created.id}`);
    } catch {
      // erreur affichée via create.error / fieldErrors API
    }
  };

  useEffect(() => {
    const id = focusTargetRef.current;
    if (!id) return;
    focusTargetRef.current = null;
    queueMicrotask(() => {
      const el = document.getElementById(id);
      if (!el) return;
      try {
        el.focus({ preventScroll: true });
      } catch {
        el.focus();
      }
      if (typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
  }, [localErrors]);

  const apiError = create.error instanceof ApiException ? create.error.apiError : null;
  const apiErrorText = getApiErrorMessage(create.error, lang);
  const fieldErrors =
    apiError?.details && typeof apiError.details === "object"
      ? (apiError.details as { fieldErrors?: Record<string, string[]> }).fieldErrors
      : undefined;

  // 9.T.4 post-smoke fix 3e itération — 409 COMPANIES_CREATE_DUPLICATE_DETECTED.
  // Le backend renvoie `details.candidates` avec les company_id existants
  // qui matchent exact (business_number OR name+postal). On rend un warning
  // panel amber + Link vers chaque candidat — l'utilisateur ouvre l'existante
  // au lieu de créer un doublon.
  const isDuplicate409 = apiError?.code === "COMPANIES_CREATE_DUPLICATE_DETECTED";
  const duplicateCandidates: DuplicateCandidate[] = isDuplicate409
    ? ((apiError?.details as { candidates?: DuplicateCandidate[] } | undefined)?.candidates ?? [])
    : [];

  // 9.T.4 post-smoke fix 4e itération — le warning duplicate est rendu en
  // haut de page. Si le broker submit depuis le sticky footer (scrollé bas
  // du form), il ne le voyait pas. On scroll + focus le warning dès qu'il
  // apparaît. `tabIndex={-1}` sur le div panel permet le focus programmatique
  // sans polluer l'ordre tab naturel.
  const duplicateWarningRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!isDuplicate409 || duplicateCandidates.length === 0) return;
    const el = duplicateWarningRef.current;
    if (!el) return;
    queueMicrotask(() => {
      if (typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      try {
        el.focus({ preventScroll: true });
      } catch {
        el.focus();
      }
    });
  }, [isDuplicate409, duplicateCandidates.length]);

  const errorMessageFor = (key: string): string | null =>
    localErrors[key]?.[0] ?? fieldErrors?.[key]?.[0] ?? null;

  const fieldError = (key: string) => {
    const msg = errorMessageFor(key);
    if (!msg) return null;
    const typeMatch = msg.match(/^Invalid format for identification_type=(\w+)$/);
    const displayed = typeMatch
      ? t("companies.fields.identification_type_error_invalid_format", {
          type: typeMatch[1],
        })
      : msg;
    return (
      <span id={`${key}-error`} className="mt-1 block text-xs text-red-600">
        {displayed}
      </span>
    );
  };

  const ariaProps = (key: string) => {
    const hasError = !!errorMessageFor(key);
    return hasError ? { "aria-invalid": true as const, "aria-describedby": `${key}-error` } : {};
  };

  const handleDetailsToggle = (section: SectionKey) => (e: SyntheticEvent<HTMLDetailsElement>) => {
    const isOpen = e.currentTarget.open;
    setOpenSections((s) => (s[section] === isOpen ? s : { ...s, [section]: isOpen }));
  };

  const navItems = [
    { anchorId: "identity", label: t("companies.create.section.identity") },
    { anchorId: "address", label: t("companies.create.section.address") },
    { anchorId: "contact", label: t("companies.create.section.contact") },
    { anchorId: "workforce", label: t("companies.create.section.workforce") },
    { anchorId: "current_plan", label: t("companies.create.section.current_plan") },
    { anchorId: "notes", label: t("companies.create.section.notes") },
  ];

  return (
    <div className="mx-auto w-full max-w-4xl pb-24 lg:max-w-screen-xl">
      {/* Sticky page header — `top-16` mobile / `lg:top-0` desktop (main scroll-container 9.P.2.R2) */}
      <header className="sticky top-16 z-10 -mx-4 mb-6 border-b border-slate-200 bg-slate-50/95 px-4 py-4 backdrop-blur-sm lg:top-0 lg:-mx-8 lg:px-8 lg:py-5">
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
          {t("companies.create.title")}
        </h1>
        <p className="mt-1 text-sm text-slate-600">{t("companies.create.subtitle")}</p>
      </header>

      {apiError && !isDuplicate409 && (
        <div
          role="alert"
          className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {apiErrorText || apiError.message || t("errors.generic")}
        </div>
      )}

      {isDuplicate409 && duplicateCandidates.length > 0 && (
        <div
          ref={duplicateWarningRef}
          role="alert"
          aria-live="polite"
          tabIndex={-1}
          className="focus-visible:ring-brand-500 mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          data-testid="company-create-duplicate-warning"
        >
          <h3 className="text-sm font-semibold text-amber-900">
            {t("companies.create.duplicate.title")}
          </h3>
          <p className="mt-1 text-xs text-amber-800">{t("companies.create.duplicate.body")}</p>
          <ul className="mt-3 space-y-2">
            {duplicateCandidates.map((c) => (
              <li
                key={c.company_id}
                className="rounded-md border border-amber-200 bg-white px-3 py-2"
                data-testid={`company-create-duplicate-candidate-${c.company_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900">{c.legal_name}</div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
                      {c.address_city && <span>{c.address_city}</span>}
                      {c.address_postal && (
                        <span className="font-mono" translate="no">
                          {c.address_postal}
                        </span>
                      )}
                      {c.business_number && (
                        <span className="font-mono" translate="no">
                          {c.business_number}
                        </span>
                      )}
                      <span className="text-amber-800">
                        {t(`companies.create.duplicate.match_kind.${c.match_kind}`)}
                      </span>
                    </div>
                  </div>
                  <Link
                    to={`/companies/${c.company_id}`}
                    className="focus-visible:ring-brand-500 border-brand-300 text-brand-800 hover:bg-brand-50 shrink-0 rounded-md border bg-white px-3 py-1.5 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                    data-testid={`company-create-duplicate-open-${c.company_id}`}
                  >
                    {t("companies.create.duplicate.open_cta")}
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 2-col layout : section nav left + form right */}
      <div className="grid gap-6 lg:grid-cols-[14rem_1fr] lg:gap-8">
        {/* Section nav sidebar — sticky desktop */}
        <div className="hidden lg:block">
          <div className="sticky top-40">
            <CompanySectionNav
              items={navItems}
              ariaLabel={t("companies.create.section_nav_label")}
            />
          </div>
        </div>

        {/* Form principal */}
        <form onSubmit={onSubmit} className="space-y-4">
          <CompanyFormDetails
            id="identity"
            title={t("companies.create.section.identity")}
            open={openSections.identity}
            onToggle={handleDetailsToggle("identity")}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block sm:col-span-2">
                <span className="label">{t("companies.fields.legal_name")} *</span>
                <input
                  id="legal_name"
                  className="input"
                  value={form.legal_name}
                  onChange={(e) => setField("legal_name", e.target.value)}
                  onBlur={handleBlur("legal_name")}
                  required
                  {...ariaProps("legal_name")}
                />
                {fieldError("legal_name")}
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.trade_name")}</span>
                <input
                  id="trade_name"
                  className="input"
                  value={form.trade_name}
                  onChange={(e) => setField("trade_name", e.target.value)}
                  onBlur={handleBlur("trade_name")}
                  {...ariaProps("trade_name")}
                />
                {fieldError("trade_name")}
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.identification_type")}</span>
                <select
                  id="identification_type"
                  className="input"
                  value={form.identification_type}
                  onChange={(e) => {
                    const newType = e.target.value as FormState["identification_type"];
                    setField("identification_type", newType);
                    if (form.business_number.trim() === "") {
                      setLocalErrors((errs) => {
                        if (!("business_number" in errs)) return errs;
                        const next = { ...errs };
                        delete next.business_number;
                        return next;
                      });
                      return;
                    }
                    if (newType === "") {
                      setLocalErrors((errs) => {
                        if (!("business_number" in errs)) return errs;
                        const next = { ...errs };
                        delete next.business_number;
                        return next;
                      });
                      return;
                    }
                    const err = Schemas.validateBusinessNumberForType(
                      form.business_number.trim(),
                      newType,
                    );
                    setLocalErrors((errs) => {
                      const next = { ...errs };
                      if (err === "invalid_format") {
                        next.business_number = [
                          `Invalid format for identification_type=${newType}`,
                        ];
                      } else {
                        delete next.business_number;
                      }
                      return next;
                    });
                  }}
                  onBlur={handleBlur("business_number")}
                >
                  <option value="">{t("companies.fields.identification_type_placeholder")}</option>
                  <option value="NEQ">
                    {t("companies.fields.identification_type_options.NEQ")}
                  </option>
                  <option value="BN">{t("companies.fields.identification_type_options.BN")}</option>
                  <option value="CORP_NUM_CA">
                    {t("companies.fields.identification_type_options.CORP_NUM_CA")}
                  </option>
                  <option value="EIN_US">
                    {t("companies.fields.identification_type_options.EIN_US")}
                  </option>
                  <option value="VAT_EU">
                    {t("companies.fields.identification_type_options.VAT_EU")}
                  </option>
                  <option value="OTHER">
                    {t("companies.fields.identification_type_options.OTHER")}
                  </option>
                </select>
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.business_number")}</span>
                <input
                  id="business_number"
                  className="input"
                  value={form.business_number}
                  onChange={(e) => setField("business_number", e.target.value)}
                  onBlur={handleBlur("business_number")}
                  {...ariaProps("business_number")}
                />
                <span className="mt-1 block text-xs text-slate-500">
                  {form.identification_type
                    ? t(
                        `companies.fields.identification_type_help_dynamic.${form.identification_type}`,
                      )
                    : t("companies.fields.business_number_help")}
                </span>
                {fieldError("business_number")}
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.industry")}</span>
                <input
                  id="industry"
                  className="input"
                  value={form.industry}
                  onChange={(e) => setField("industry", e.target.value)}
                  onBlur={handleBlur("industry")}
                  {...ariaProps("industry")}
                />
                {fieldError("industry")}
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.founded_year")}</span>
                <input
                  id="founded_year"
                  type="number"
                  min={1800}
                  max={new Date().getFullYear()}
                  className="input"
                  value={form.founded_year}
                  onChange={(e) => setField("founded_year", e.target.value)}
                  onBlur={handleBlur("founded_year")}
                  {...ariaProps("founded_year")}
                />
                {fieldError("founded_year")}
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.language")}</span>
                <select
                  id="language"
                  className="input"
                  value={form.language}
                  onChange={(e) => setField("language", e.target.value as "fr" | "en")}
                >
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                </select>
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.employee_count")}</span>
                <input
                  id="employee_count"
                  type="number"
                  min={0}
                  className="input"
                  value={form.employee_count}
                  onChange={(e) => setField("employee_count", e.target.value)}
                  onBlur={handleBlur("employee_count")}
                  {...ariaProps("employee_count")}
                />
                {fieldError("employee_count")}
              </label>
            </div>
          </CompanyFormDetails>

          <CompanyFormDetails
            id="address"
            title={t("companies.create.section.address")}
            open={openSections.address}
            onToggle={handleDetailsToggle("address")}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block sm:col-span-2">
                <span className="label">{t("companies.fields.address_street")}</span>
                <input
                  id="address_street"
                  className="input"
                  value={form.address_street}
                  onChange={(e) => setField("address_street", e.target.value)}
                  onBlur={handleBlur("address_street")}
                  {...ariaProps("address_street")}
                />
                {fieldError("address_street")}
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.address_city")}</span>
                <input
                  id="address_city"
                  className="input"
                  value={form.address_city}
                  onChange={(e) => setField("address_city", e.target.value)}
                  onBlur={handleBlur("address_city")}
                  {...ariaProps("address_city")}
                />
                {fieldError("address_city")}
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.address_province")}</span>
                <input
                  id="address_province"
                  className="input"
                  value={form.address_province}
                  onChange={(e) => setField("address_province", e.target.value)}
                  onBlur={handleBlur("address_province")}
                  {...ariaProps("address_province")}
                />
                {fieldError("address_province")}
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.address_postal")}</span>
                <input
                  id="address_postal"
                  className="input"
                  value={form.address_postal}
                  onChange={(e) => setField("address_postal", e.target.value)}
                  onBlur={handleBlur("address_postal")}
                  {...ariaProps("address_postal")}
                />
                {fieldError("address_postal")}
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.address_country")}</span>
                <input
                  id="address_country"
                  className="input"
                  value={form.address_country}
                  onChange={(e) => setField("address_country", e.target.value)}
                  onBlur={handleBlur("address_country")}
                  {...ariaProps("address_country")}
                />
                {fieldError("address_country")}
              </label>
            </div>
          </CompanyFormDetails>

          <CompanyFormDetails
            id="contact"
            title={t("companies.create.section.contact")}
            open={openSections.contact}
            onToggle={handleDetailsToggle("contact")}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="label">{t("companies.fields.phone")}</span>
                <input
                  id="phone"
                  className="input"
                  value={form.phone}
                  onChange={(e) => setField("phone", e.target.value)}
                  onBlur={handleBlur("phone")}
                  {...ariaProps("phone")}
                />
                {fieldError("phone")}
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.fax")}</span>
                <input
                  id="fax"
                  className="input"
                  value={form.fax}
                  onChange={(e) => setField("fax", e.target.value)}
                  onBlur={handleBlur("fax")}
                  {...ariaProps("fax")}
                />
                {fieldError("fax")}
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.email")}</span>
                <input
                  id="email"
                  type="email"
                  className="input"
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  onBlur={handleBlur("email")}
                  {...ariaProps("email")}
                />
                {fieldError("email")}
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.website")}</span>
                <input
                  id="website"
                  className="input"
                  value={form.website}
                  onChange={(e) => setField("website", e.target.value)}
                  onBlur={handleBlur("website")}
                  {...ariaProps("website")}
                />
                {fieldError("website")}
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.plan_admin_name")}</span>
                <input
                  id="plan_admin_name"
                  className="input"
                  value={form.plan_admin_name}
                  onChange={(e) => setField("plan_admin_name", e.target.value)}
                  onBlur={handleBlur("plan_admin_name")}
                  {...ariaProps("plan_admin_name")}
                />
                {fieldError("plan_admin_name")}
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.plan_admin_email")}</span>
                <input
                  id="plan_admin_email"
                  type="email"
                  className="input"
                  value={form.plan_admin_email}
                  onChange={(e) => setField("plan_admin_email", e.target.value)}
                  onBlur={handleBlur("plan_admin_email")}
                  {...ariaProps("plan_admin_email")}
                />
                {fieldError("plan_admin_email")}
              </label>
            </div>
          </CompanyFormDetails>

          <CompanyFormDetails
            id="workforce"
            title={t("companies.create.section.workforce")}
            open={openSections.workforce}
            onToggle={handleDetailsToggle("workforce")}
          >
            <div className="space-y-3">
              <Checkbox
                checked={form.has_seasonal}
                onChange={(v) => setField("has_seasonal", v)}
                label={t("companies.fields.has_seasonal")}
              />
              <Checkbox
                checked={form.has_commission}
                onChange={(v) => setField("has_commission", v)}
                label={t("companies.fields.has_commission")}
              />
              <Checkbox
                checked={form.has_unionized}
                onChange={(v) => setField("has_unionized", v)}
                label={t("companies.fields.has_unionized")}
              />
              <Checkbox
                checked={form.has_remote_abroad}
                onChange={(v) => setField("has_remote_abroad", v)}
                label={t("companies.fields.has_remote_abroad")}
              />
              <Checkbox
                checked={form.considers_remote_abroad}
                onChange={(v) => setField("considers_remote_abroad", v)}
                label={t("companies.fields.considers_remote_abroad")}
              />
              <Checkbox
                checked={form.family_ties}
                onChange={(v) => setField("family_ties", v)}
                label={t("companies.fields.family_ties")}
              />
              {form.family_ties && (
                <label className="block">
                  <span className="label">{t("companies.fields.family_ties_note")}</span>
                  <textarea
                    id="family_ties_note"
                    className="input min-h-[4rem]"
                    value={form.family_ties_note}
                    onChange={(e) => setField("family_ties_note", e.target.value)}
                    onBlur={handleBlur("family_ties_note")}
                    {...ariaProps("family_ties_note")}
                  />
                  {fieldError("family_ties_note")}
                </label>
              )}
            </div>
          </CompanyFormDetails>

          <CompanyFormDetails
            id="current_plan"
            title={t("companies.create.section.current_plan")}
            open={openSections.current_plan}
            onToggle={handleDetailsToggle("current_plan")}
          >
            <div className="space-y-3">
              <Checkbox
                checked={form.has_current_plan}
                onChange={(v) => setField("has_current_plan", v)}
                label={t("companies.fields.has_current_plan")}
              />
              {form.has_current_plan && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="label">{t("companies.fields.current_insurer")}</span>
                    <input
                      id="current_insurer"
                      className="input"
                      value={form.current_insurer}
                      onChange={(e) => setField("current_insurer", e.target.value)}
                      onBlur={handleBlur("current_insurer")}
                      {...ariaProps("current_insurer")}
                    />
                    {fieldError("current_insurer")}
                  </label>
                  <label className="block">
                    <span className="label">{t("companies.fields.current_policy_number")}</span>
                    <input
                      id="current_policy_number"
                      className="input"
                      value={form.current_policy_number}
                      onChange={(e) => setField("current_policy_number", e.target.value)}
                      onBlur={handleBlur("current_policy_number")}
                      {...ariaProps("current_policy_number")}
                    />
                    {fieldError("current_policy_number")}
                  </label>
                  <label className="block">
                    <span className="label">{t("companies.fields.current_since_date")}</span>
                    <input
                      id="current_since_date"
                      type="date"
                      className="input"
                      value={form.current_since_date}
                      onChange={(e) => setField("current_since_date", e.target.value)}
                      onBlur={handleBlur("current_since_date")}
                      {...ariaProps("current_since_date")}
                    />
                    {fieldError("current_since_date")}
                  </label>
                  <label className="block">
                    <span className="label">{t("companies.fields.current_renewal_date")}</span>
                    <input
                      id="current_renewal_date"
                      type="date"
                      className="input"
                      value={form.current_renewal_date}
                      onChange={(e) => setField("current_renewal_date", e.target.value)}
                      onBlur={handleBlur("current_renewal_date")}
                      {...ariaProps("current_renewal_date")}
                    />
                    {fieldError("current_renewal_date")}
                  </label>
                </div>
              )}
            </div>
          </CompanyFormDetails>

          <CompanyFormDetails
            id="notes"
            title={t("companies.create.section.notes")}
            open={openSections.notes}
            onToggle={handleDetailsToggle("notes")}
          >
            <label className="block">
              <span className="label">{t("companies.fields.notes")}</span>
              <textarea
                id="notes"
                className="input min-h-[6rem]"
                value={form.notes}
                onChange={(e) => setField("notes", e.target.value)}
                onBlur={handleBlur("notes")}
                {...ariaProps("notes")}
              />
              {fieldError("notes")}
            </label>
          </CompanyFormDetails>

          {/* Sticky save bar bottom */}
          <div className="sticky bottom-0 z-10 -mx-4 mt-6 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur-sm lg:-mx-8 lg:px-8">
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onCancel}
                disabled={create.isPending}
                className="focus-visible:ring-brand-500 touch-manipulation rounded-lg px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
              >
                {t("common.cancel")}
              </button>
              <button
                type="submit"
                disabled={create.isPending || !form.legal_name.trim()}
                className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {create.isPending ? t("app.loading") : t("common.save")}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function Checkbox({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex items-start gap-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="text-brand-600 focus:ring-brand-500 mt-1 h-4 w-4 rounded border-slate-300"
      />
      <span className="text-sm text-slate-700">{label}</span>
    </label>
  );
}
